/**
 * Alkisen v1 API client. Talks to /api/v1/* on the Fastify backend.
 *
 * Response envelope: { ok: true, data: {...}, meta?: { page, limit, total } }
 */
import { api, apiBase } from './client';
import type { BarcodeResolveResult } from './types';

// ─── Common types (mirror backend cellar-repository.ts) ───────────────
export type BottleStatus = 'cellared' | 'drunk' | 'gifted';
export type BottleCategory = 'wine' | 'beer' | 'spirits' | 'cider' | 'sake' | 'mead' | 'other';
export type Role = 'OWNER' | 'MEMBER' | 'GUEST';

export interface VkUser {
  id: string;
  email: string;
  displayName: string;
  prefs?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  /** Default markup multiplier the "Prisautomat" uses to suggest selling prices (default 2.8). */
  pricingMarkup: number;
  /** Subscription/entitlement system — see lib/entitlements.ts + docs/BUSINESS_MODEL.md. */
  planValidUntil?: string;
  entitlementsOverride?: Record<string, unknown> | null;
  stripeCustomerId?: string;
  createdAt: string;
  updatedAt: string;
  etag: string;
}

export interface Membership {
  id: string;
  tenantId: string;
  userId: string;
  email: string;
  role: Role;
  joinedAt: string;
  createdAt: string;
}

export interface Bottle {
  id: string;
  tenantId: string;
  catalogId?: string;
  productId?: string;
  customName?: string;
  name: string;
  producer?: string;
  vintage?: string;
  region?: string;
  country?: string;
  grapeVarieties: string[];
  qty: number;
  status: BottleStatus;
  category: BottleCategory;
  /**
   * Best-effort wine style (sparkling/white/rose/red/sweet_fortified)
   * resolved server-side from the linked wine_catalog row's own category —
   * see backend services/wine-list-service.ts `resolveWineStyle`. Only set
   * for wine-category bottles with a catalog match; undefined otherwise.
   */
  catalogWineStyle?: 'sparkling' | 'white' | 'rose' | 'red' | 'sweet_fortified';
  rackId?: string;
  slot?: string;
  /** Storage-location assignment — see StorageLocation. Undefined = "Uplassert". */
  locationId?: string;
  /** "Venter på levering" — ordered/expected but not yet physically located. */
  pendingDelivery: boolean;
  barcode?: string;
  photoUrl?: string;
  notes?: string;
  drinkFromYear?: number;
  drinkToYear?: number;
  tags: string[];
  metadata: Record<string, unknown>;
  /** Vinliste (wine list) menu pricing — set explicitly, separate from any purchase price. */
  sellingPriceNok?: number;
  glassPriceNok?: number;
  glassSizeMl?: number;
  availableByGlass: boolean;
  /** Pricing foundation — what the owner paid. */
  purchasePriceNok?: number;
  purchasePriceExVatNok?: number;
  /** When set, the "Prisautomat" bulk-apply actions never touch sellingPriceNok on this bottle. */
  lockSellingPrice: boolean;
  supplier?: string;
  addedAt: string;
  createdAt: string;
  updatedAt: string;
  etag: string;
}

export interface Rack {
  id: string;
  tenantId: string;
  name: string;
  layout: { rows: number; cols: number };
  parentId?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  etag: string;
}

// ─── Storage locations ("lager/hylleplassering") ───────────────────────
// Supersedes `Rack` for the cellar UI — see backend store/cellar-repository.ts.
export type StorageLocationKind = 'room' | 'cabinet' | 'fridge' | 'shelf' | 'freezer' | 'other';

export interface StorageLocation {
  id: string;
  tenantId: string;
  parentId?: string;
  name: string;
  kind: StorageLocationKind;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  etag: string;
}

export interface LocationValueSummary {
  /** Distinct bottle rows ("wines"). */
  bottleCount: number;
  /** Sum of qty — physical bottle count. */
  totalQty: number;
  purchaseValueNok: number;
  sellingValueNok: number;
}

export interface StorageLocationTreeNode extends StorageLocation, LocationValueSummary {
  children: StorageLocationTreeNode[];
}

export interface LocationsOverview {
  tree: StorageLocationTreeNode[];
  all: LocationValueSummary;
  unassigned: LocationValueSummary;
  pendingDelivery: LocationValueSummary;
}

export interface Occasion {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  mood?: string;
  criteria: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  etag: string;
}

export interface TastingNote {
  id: string;
  tenantId: string;
  bottleId: string;
  userId: string;
  rating1to5: number;
  drunkAt?: string;
  withFood?: string;
  comment?: string;
  createdAt: string;
  updatedAt: string;
}

export type ScanKind = 'qr' | 'barcode' | 'label-photo' | 'recipe-text' | 'recipe-photo' | 'food-photo';

export interface ScanEvent {
  id: string;
  tenantId: string;
  kind: ScanKind;
  status: 'queued' | 'processing' | 'ready' | 'failed';
  symbology?: string;
  code?: string;
  input?: Record<string, unknown>;
  aiOutput?: Record<string, unknown>;
  confidence?: number;
  matchedBottleId?: string;
  matchedCatalogId?: string;
  suggestedBottleIds: string[];
  aiCostUsd?: number;
  createdAt: string;
}

export interface PairingSession {
  id: string;
  tenantId: string;
  kind: 'recipe' | 'plate';
  status: 'queued' | 'processing' | 'ready' | 'failed';
  extractedDish?: {
    cuisine?: string;
    weight?: string;
    sauce?: string;
    flavors?: string[];
    cookingMethod?: string;
    summary?: string;
    rawText?: string;
    proteins?: string[];
    methods?: string[];
  };
  suggestions?: { bottleId: string; score: number; reasoning?: string }[];
  createdAt: string;
}

export interface RecommendationItem {
  bottleId: string;
  score: number;
  reasons: string[];
  explanation?: string;
  source?: 'rules' | 'openai';
}

export interface PersonalToken {
  id: string;
  userId: string;
  name: string;
  prefix: string;
  expiresAt?: string;
  createdAt: string;
  token?: string; // only on create
}

export interface Job {
  id: string;
  tenantId: string;
  type: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'processing' | 'done';
  payload?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  attempts?: number;
  maxAttempts?: number;
  runAfter?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Invitation {
  id: string;
  tenantId: string;
  email: string;
  role: Role;
  token: string;
  invitedBy: string;
  expiresAt: string;
  acceptedAt?: string;
  createdAt: string;
}

// ─── Envelope unwrap helper ───────────────────────────────────────────
async function v1<T>(promise: Promise<{ ok: true; data: T; meta?: { page: number; limit: number; total: number } }>): Promise<T & { _meta?: { page: number; limit: number; total: number } }> {
  const res = await promise;
  return Object.assign({}, res.data, { _meta: res.meta }) as T & { _meta?: { page: number; limit: number; total: number } };
}

// ─── Auth & me ────────────────────────────────────────────────────────
export const Auth = {
  requestCode: (email: string) =>
    api<{ ok: true; data: { sent: boolean; cooldownSeconds?: number } }>(
      '/api/v1/auth/request-code',
      { method: 'POST', body: { email } },
    ).then((r) => r.data),

  verifyCode: (email: string, code: string) =>
    api<{ ok: true; data: { user: VkUser; memberships: Membership[]; defaultTenantId?: string } }>(
      '/api/v1/auth/verify-code',
      { method: 'POST', body: { email, code } },
    ).then((r) => r.data),

  logout: () => api<unknown>('/api/v1/auth/logout', { method: 'POST' }),

  me: () =>
    api<{ ok: true; data: { user: VkUser; memberships: Membership[]; defaultTenantId?: string; role?: string } }>(
      '/api/v1/me',
    ).then((r) => r.data),

  createPersonalToken: (payload: { name: string; expiresInDays?: number }) =>
    api<{ ok: true; data: { token: PersonalToken } }>('/api/v1/me/personal-tokens', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.token),

  listPersonalTokens: () =>
    api<{ ok: true; data: { tokens: PersonalToken[] } }>('/api/v1/me/personal-tokens')
      .then((r) => r.data.tokens),

  deletePersonalToken: (id: string) =>
    api<unknown>(`/api/v1/me/personal-tokens/${id}`, { method: 'DELETE' }),

  updateMe: (payload: { displayName?: string }) =>
    api<{ ok: true; data: { user: VkUser } }>('/api/v1/me', {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.user),

  updatePrefs: (prefs: Record<string, unknown>) =>
    api<{ ok: true; data: { user: VkUser } }>('/api/v1/me/prefs', {
      method: 'PATCH',
      body: { prefs },
    }).then((r) => r.data.user),
};

export const Realtime = {
  session: () =>
    api<{ ok: true; data: { token: string; tenantId: string; tenantIds: string[]; expiresInSeconds: number } }>(
      '/api/v1/realtime/session',
    ).then((r) => r.data),
};

// ─── Tenants ──────────────────────────────────────────────────────────
export const Tenants = {
  list: () =>
    api<{ ok: true; data: { tenants: Tenant[]; memberships: Membership[] } }>('/api/v1/tenants').then((r) => r.data),

  create: (payload: { name: string; slug?: string }) =>
    api<{ ok: true; data: { tenant: Tenant; membership: Membership } }>('/api/v1/tenants', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data),

  update: (id: string, payload: { name?: string; slug?: string; plan?: string; pricingMarkup?: number }, etag?: string) =>
    api<{ ok: true; data: { tenant: Tenant } }>(`/api/v1/tenants/${id}`, {
      method: 'PATCH',
      body: payload,
      headers: etag ? { 'If-Match': etag } : undefined,
    }).then((r) => r.data.tenant),

  delete: (id: string) => api<unknown>(`/api/v1/tenants/${id}`, { method: 'DELETE' }),

  members: (id: string) =>
    api<{ ok: true; data: { members: Membership[] } }>(`/api/v1/tenants/${id}/members`).then((r) => r.data.members),

  invite: (id: string, payload: { email: string; role?: Role }) =>
    api<{ ok: true; data: { invitation: { id: string; token: string; expiresAt: string } } }>(
      `/api/v1/tenants/${id}/invitations`,
      { method: 'POST', body: payload },
    ).then((r) => r.data.invitation),

  acceptInvite: (token: string) =>
    api<{ ok: true; data: { tenant: Tenant; membership: Membership } }>(
      `/api/v1/invitations/${token}/accept`,
      { method: 'POST' },
    ).then((r) => r.data),

  removeMember: (tenantId: string, userId: string) =>
    api<unknown>(`/api/v1/tenants/${tenantId}/members/${userId}`, { method: 'DELETE' }),

  updateMemberRole: (tenantId: string, userId: string, role: Role) =>
    api<{ ok: true; data: { member: Membership } }>(`/api/v1/tenants/${tenantId}/members/${userId}`, {
      method: 'PATCH',
      body: { role },
    }).then((r) => r.data.member),

  invitations: (tenantId: string, includeAccepted = false) =>
    api<{ ok: true; data: { invitations: Invitation[] } }>(
      `/api/v1/tenants/${tenantId}/invitations`,
      { query: includeAccepted ? { includeAccepted: 'true' } : undefined },
    ).then((r) => r.data.invitations),

  revokeInvitation: (tenantId: string, invitationId: string) =>
    api<unknown>(`/api/v1/tenants/${tenantId}/invitations/${invitationId}`, { method: 'DELETE' }),
};

// ─── Admin (superadmin-only) ────────────────────────────────────────────
export const Admin = {
  /** SUPER_ADMIN only — manual plan override, see routes/auth.ts adminPlanBodySchema. */
  setTenantPlan: (
    tenantId: string,
    payload: { plan?: string; planValidUntil?: string | null; entitlementsOverride?: Record<string, unknown> | null },
  ) =>
    api<{ ok: true; data: { tenant: Tenant } }>(`/api/v1/admin/tenants/${tenantId}/plan`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.tenant),
};

// ─── Billing / abonnement ────────────────────────────────────────────────
export interface BillingStatus {
  enabled: boolean;
  plans: {
    pluss: { monthlyNok: number; annualNok: number };
    pro: { monthlyNok: number; annualNok: number };
  };
}

export type BillingPlan = 'pluss' | 'pro';
export type BillingInterval = 'monthly' | 'annual';

export const Billing = {
  status: () => api<{ ok: true; data: BillingStatus }>('/api/v1/billing/status').then((r) => r.data),

  checkout: (plan: BillingPlan, interval: BillingInterval) =>
    api<{ ok: true; data: { checkoutUrl: string } }>('/api/v1/billing/checkout', {
      method: 'POST',
      body: { plan, interval },
    }).then((r) => r.data),

  portal: () =>
    api<{ ok: true; data: { portalUrl: string } }>('/api/v1/billing/portal', { method: 'POST' }).then((r) => r.data),
};

// ─── Bottles ──────────────────────────────────────────────────────────
export interface BottleListQuery {
  q?: string;
  region?: string;
  grape?: string;
  status?: BottleStatus;
  category?: BottleCategory;
  rackId?: string;
  /** Exact single-location filter. */
  locationId?: string;
  /** Comma-separated location ids (a node + all its descendants). */
  locationIds?: string;
  /** "Uplassert" system view. */
  unassigned?: boolean;
  /** "Venter på levering" system view. */
  pendingDelivery?: boolean;
  drinkBy?: number;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  include?: string;
}
export interface BottleCreate {
  catalogId?: string;
  productId?: string;
  customName?: string;
  name?: string;
  producer?: string;
  vintage?: string;
  region?: string;
  country?: string;
  grapeVarieties?: string[];
  qty?: number;
  status?: BottleStatus;
  category?: BottleCategory;
  rackId?: string;
  slot?: string;
  locationId?: string;
  pendingDelivery?: boolean;
  barcode?: string;
  photoUrl?: string;
  notes?: string;
  drinkFromYear?: number;
  drinkToYear?: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
  sellingPriceNok?: number;
  glassPriceNok?: number;
  glassSizeMl?: number;
  availableByGlass?: boolean;
  purchasePriceNok?: number;
  purchasePriceExVatNok?: number;
  lockSellingPrice?: boolean;
  supplier?: string;
}

export const Bottles = {
  list: (query: BottleListQuery = {}) =>
    api<{ ok: true; data: { bottles: Bottle[] }; meta: { page: number; limit: number; total: number } }>(
      '/api/v1/bottles',
      { query: query as Record<string, string | number | undefined> },
    ).then((r) => ({ bottles: r.data.bottles, meta: r.meta })),

  get: (id: string, include?: string) =>
    api<{ ok: true; data: { bottle: Bottle } }>(`/api/v1/bottles/${id}`, { query: { include } }).then(
      (r) => r.data.bottle,
    ),

  create: (payload: BottleCreate) =>
    api<{ ok: true; data: { bottle: Bottle } }>('/api/v1/bottles', { method: 'POST', body: payload }).then(
      (r) => r.data.bottle,
    ),

  update: (id: string, payload: Partial<BottleCreate>, etag?: string) =>
    api<{ ok: true; data: { bottle: Bottle } }>(`/api/v1/bottles/${id}`, {
      method: 'PATCH',
      body: payload,
      headers: etag ? { 'If-Match': etag } : undefined,
    }).then((r) => r.data.bottle),

  delete: (id: string) => api<unknown>(`/api/v1/bottles/${id}`, { method: 'DELETE' }),

  move: (id: string, payload: { rackId?: string; slot?: string }) =>
    api<{ ok: true; data: { bottle: Bottle } }>(`/api/v1/bottles/${id}/move`, {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.bottle),

  drink: (id: string, payload: { drunkAt?: string; withFood?: string; rating1to5?: number; comment?: string }) =>
    api<{ ok: true; data: { bottle: Bottle; note?: TastingNote } }>(`/api/v1/bottles/${id}/drink`, {
      method: 'POST',
      body: payload,
    }).then((r) => r.data),

  /** Manual "Selg" — the session-authed counterpart to the POS sales-feed ingest (see lib/api/v1.ts Pos.ingest). Decrements qty (floored at 0) and emits a 'sold' kjellerhistorikk event. */
  sell: (id: string, payload: { quantity?: number; unitPriceNok?: number; note?: string }) =>
    api<{ ok: true; data: { bottle: Bottle; appliedQuantity: number; requestedQuantity: number } }>(
      `/api/v1/bottles/${id}/sell`,
      { method: 'POST', body: payload },
    ).then((r) => r.data),

  bulk: (op: 'move' | 'move-location' | 'delete' | 'tag', ids: string[], payload: Record<string, unknown> = {}) =>
    api<{ ok: true; data: { affected?: number; deleted?: number; bottles?: Bottle[] } }>('/api/v1/bottles/bulk', {
      method: 'POST',
      body: { op, ids, payload },
    }).then((r) => r.data),

  /**
   * Explicit storage-location assign/move — unlike `update()`, which
   * coalesces every field, this sets `locationId` to exactly what's passed
   * (including `null` to unassign back to "Uplassert").
   */
  assignLocation: (id: string, payload: { locationId?: string | null; pendingDelivery?: boolean }) =>
    api<{ ok: true; data: { bottle: Bottle } }>(`/api/v1/bottles/${id}/location`, {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.bottle),

  bulkMoveLocation: (ids: string[], payload: { locationId?: string | null; pendingDelivery?: boolean }) =>
    api<{ ok: true; data: { bottles: Bottle[] } }>('/api/v1/bottles/bulk', {
      method: 'POST',
      body: { op: 'move-location', ids, payload },
    }).then((r) => r.data.bottles),

  /** Aggregated stats for a filter selection — the Kjeller stats header. */
  stats: (query: BottleListQuery = {}) =>
    api<{ ok: true; data: { stats: LocationValueSummary } }>('/api/v1/bottles/stats', {
      query: query as Record<string, string | number | boolean | undefined>,
    }).then((r) => r.data.stats),

  notes: (id: string) =>
    api<{ ok: true; data: { notes: TastingNote[] } }>(`/api/v1/bottles/${id}/notes`).then((r) => r.data.notes),

  addNote: (id: string, payload: { rating1to5: number; comment?: string; drunkAt?: string; withFood?: string }) =>
    api<{ ok: true; data: { note: TastingNote } }>(`/api/v1/bottles/${id}/notes`, {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.note),
};

export const Notes = {
  update: (id: string, payload: Partial<{ rating1to5: number; comment: string; drunkAt: string; withFood: string }>) =>
    api<{ ok: true; data: { note: TastingNote } }>(`/api/v1/notes/${id}`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.note),
  delete: (id: string) => api<unknown>(`/api/v1/notes/${id}`, { method: 'DELETE' }),
};

// ─── Pricing ("Prisautomat") ────────────────────────────────────────────
export interface PricingOverviewRow {
  bottle: Bottle;
  catalogPriceNok?: number;
  catalogPriceUpdatedAt?: string;
  suggestedPriceNok?: number;
  sellBuyRatioPct?: number;
  costOfGoodsPct?: number;
  surchargePct?: number;
}

export type PricingBulkAction =
  | { action: 'set-price'; ids: string[]; sellingPriceNok: number }
  | { action: 'apply-suggested'; ids: string[] }
  | { action: 'apply-markup'; ids: string[]; markup: number };

export const Pricing = {
  overview: () =>
    api<{ ok: true; data: { bottles: PricingOverviewRow[]; pricingMarkup: number } }>('/api/v1/pricing/overview').then(
      (r) => r.data,
    ),

  bulk: (payload: PricingBulkAction) =>
    api<{ ok: true; data: { updated: Bottle[]; skippedLocked: string[]; skippedNoBasis: string[] } }>(
      '/api/v1/pricing/bulk',
      { method: 'PATCH', body: payload },
    ).then((r) => r.data),
};

// ─── Racks ────────────────────────────────────────────────────────────
export const Racks = {
  list: (include?: string) =>
    api<{ ok: true; data: { racks: Rack[] } }>('/api/v1/racks', { query: { include } }).then((r) => r.data.racks),

  create: (payload: { name: string; layout?: { rows: number; cols: number }; parentId?: string; sortOrder?: number }) =>
    api<{ ok: true; data: { rack: Rack } }>('/api/v1/racks', { method: 'POST', body: payload }).then(
      (r) => r.data.rack,
    ),

  update: (id: string, payload: Partial<{ name: string; layout: { rows: number; cols: number }; sortOrder: number }>) =>
    api<{ ok: true; data: { rack: Rack } }>(`/api/v1/racks/${id}`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.rack),

  delete: (id: string) => api<unknown>(`/api/v1/racks/${id}`, { method: 'DELETE' }),
};

// ─── Storage locations ("lager/hylleplassering") ───────────────────────
export const Locations = {
  list: () =>
    api<{ ok: true; data: { locations: StorageLocation[] } }>('/api/v1/locations').then((r) => r.data.locations),

  tree: () =>
    api<{ ok: true; data: LocationsOverview }>('/api/v1/locations/tree').then((r) => r.data),

  create: (payload: { name: string; kind: StorageLocationKind; parentId?: string; sortOrder?: number }) =>
    api<{ ok: true; data: { location: StorageLocation } }>('/api/v1/locations', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.location),

  update: (
    id: string,
    payload: Partial<{ name: string; kind: StorageLocationKind; parentId: string | null; sortOrder: number }>,
    etag?: string,
  ) =>
    api<{ ok: true; data: { location: StorageLocation } }>(`/api/v1/locations/${id}`, {
      method: 'PATCH',
      body: payload,
      headers: etag ? { 'If-Match': etag } : undefined,
    }).then((r) => r.data.location),

  delete: (id: string, etag?: string) =>
    api<unknown>(`/api/v1/locations/${id}`, { method: 'DELETE', headers: etag ? { 'If-Match': etag } : undefined }),

  reorder: (orderedIds: string[], parentId?: string) =>
    api<{ ok: true; data: { locations: StorageLocation[] } }>('/api/v1/locations/reorder', {
      method: 'POST',
      body: { orderedIds, parentId },
    }).then((r) => r.data.locations),
};

// ─── Occasions ────────────────────────────────────────────────────────
export const Occasions = {
  list: () =>
    api<{ ok: true; data: { occasions: Occasion[] } }>('/api/v1/occasions').then((r) => r.data.occasions),

  create: (payload: { name: string; description?: string; mood?: string; criteria?: Record<string, unknown> }) =>
    api<{ ok: true; data: { occasion: Occasion } }>('/api/v1/occasions', { method: 'POST', body: payload }).then(
      (r) => r.data.occasion,
    ),

  update: (id: string, payload: Partial<{ name: string; description: string; mood: string; criteria: Record<string, unknown> }>) =>
    api<{ ok: true; data: { occasion: Occasion } }>(`/api/v1/occasions/${id}`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.occasion),

  delete: (id: string) => api<unknown>(`/api/v1/occasions/${id}`, { method: 'DELETE' }),

  matches: (id: string) =>
    api<{ ok: true; data: { bottles: Bottle[] } }>(`/api/v1/occasions/${id}/matches`).then((r) => r.data.bottles),

  pin: (id: string, bottleIds: string[]) =>
    api<{ ok: true; data: { added: number } }>(`/api/v1/occasions/${id}/bottles`, {
      method: 'POST',
      body: { bottleIds },
    }).then((r) => r.data),

  unpin: (id: string, bottleId: string) =>
    api<unknown>(`/api/v1/occasions/${id}/bottles/${bottleId}`, { method: 'DELETE' }),
};

// ─── Vinliste (wine list generator) ────────────────────────────────────
export type WineListSorting = 'producer_price' | 'producer_az' | 'subregion';
export type WineListHeaderType = 'logo' | 'title' | 'none';
export type WineListBottleSizeNaming = 'liters' | 'full_name' | 'name_liters' | 'short';
export type WineListGlassSectionMode = 'off' | 'by_style' | 'by_style_country';
export type WineListFontChoice =
  | 'Playfair Display'
  | 'Inter'
  | 'Bodoni Moda'
  | 'Lexend'
  | 'Cormorant Garamond'
  | 'Source Serif 4';

export interface WineListCategorySetting {
  key: string;
  label: string;
  visible: boolean;
  pageBreakBefore: boolean;
}

export interface WineListSettings {
  header: { type: WineListHeaderType; titleText: string };
  sorting: WineListSorting;
  categories: WineListCategorySetting[];
  showGlassPrice: boolean;
  showPrivateNote: boolean;
  repeatCategoryTitlePerPage: boolean;
  showQrCode: boolean;
  showPageNumbers: boolean;
  hideCategoryLines: boolean;
  hideWineLines: boolean;
  extraPrintMargin: boolean;
  footnoteText: string;
  fonts: { title: WineListFontChoice; subtitle: WineListFontChoice; body: WineListFontChoice; size: 'small' | 'normal' | 'large' };
  bottleSizeNaming: WineListBottleSizeNaming;
  glassSection: WineListGlassSectionMode;
}

export interface WineList {
  id: string;
  tenantId: string;
  name: string;
  settings: WineListSettings;
  shareSlug: string;
  shareUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface WineListRenderWine {
  bottleId: string;
  vintage?: string;
  name: string;
  sizeLabel?: string;
  glassPriceNok?: number;
  bottlePriceNok?: number;
  note?: string;
}

export interface WineListRenderProducerGroup {
  producer: string;
  wines: WineListRenderWine[];
}

export interface WineListRenderRegionGroup {
  country?: string;
  region?: string;
  producers: WineListRenderProducerGroup[];
}

export interface WineListRenderCategory {
  key: string;
  label: string;
  pageBreakBefore: boolean;
  bottleCount: number;
  groups: WineListRenderRegionGroup[];
}

export interface WineListGlassEntry {
  style: string;
  styleLabel: string;
  country?: string;
  producer?: string;
  name: string;
  vintage?: string;
  glassPriceNok?: number;
}

export interface WineListRender {
  id: string;
  name: string;
  header: { type: WineListHeaderType; titleText: string };
  glassSection: WineListGlassEntry[];
  categories: WineListRenderCategory[];
  footnoteText: string;
  showQrCode: boolean;
  showPageNumbers: boolean;
  shareUrl: string;
  generatedAt: string;
  totalBottleCount: number;
  /** The list's font choices — same settings the PDF embeds, so the live preview can match it exactly. */
  fonts: WineListSettings['fonts'];
}

/** Print-template preset id — see backend services/wine-list-settings.ts WINE_LIST_TEMPLATES. */
export type WineListTemplateId = 'klassisk' | 'moderne' | 'minimal';

export interface WineListTemplateInfo {
  id: WineListTemplateId;
  label: string;
  description: string;
}

export const WineLists = {
  list: () =>
    api<{ ok: true; data: { wineLists: WineList[] } }>('/api/v1/wine-lists').then((r) => r.data.wineLists),

  get: (id: string) =>
    api<{ ok: true; data: { wineList: WineList } }>(`/api/v1/wine-lists/${id}`).then((r) => r.data.wineList),

  /** Utskriftsmaler tilgjengelig i "Ny vinliste"-dialogen. */
  templates: () =>
    api<{ ok: true; data: { templates: WineListTemplateInfo[] } }>('/api/v1/wine-lists/templates').then(
      (r) => r.data.templates,
    ),

  create: (payload: { name: string; templateId?: WineListTemplateId }) =>
    api<{ ok: true; data: { wineList: WineList } }>('/api/v1/wine-lists', { method: 'POST', body: payload }).then(
      (r) => r.data.wineList,
    ),

  update: (id: string, payload: Partial<{ name: string; settings: Partial<WineListSettings> }>) =>
    api<{ ok: true; data: { wineList: WineList } }>(`/api/v1/wine-lists/${id}`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.wineList),

  delete: (id: string) => api<unknown>(`/api/v1/wine-lists/${id}`, { method: 'DELETE' }),

  rotateShareLink: (id: string) =>
    api<{ ok: true; data: { wineList: WineList } }>(`/api/v1/wine-lists/${id}/share/rotate`, {
      method: 'POST',
    }).then((r) => r.data.wineList),

  render: (id: string) =>
    api<{ ok: true; data: { render: WineListRender } }>(`/api/v1/wine-lists/${id}/render`).then((r) => r.data.render),

  pdfUrl: (id: string) => `${apiBase}/api/v1/wine-lists/${id}/pdf`,
};

// ─── Tenant profile (public restaurant page, "/profil" + "/p/:slug") ──────
export interface TenantProfile {
  tenantId: string;
  profileSlug: string | null;
  displayName: string;
  description: string;
  address: string;
  heroImageUrl: string | null;
  bookingUrl: string | null;
  websiteUrl: string | null;
  menuUrl: string | null;
  instagramUrl: string | null;
  isPublished: boolean;
  publicUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TenantProfilePatch {
  profileSlug?: string | null;
  displayName?: string;
  description?: string;
  address?: string;
  bookingUrl?: string | null;
  websiteUrl?: string | null;
  menuUrl?: string | null;
  instagramUrl?: string | null;
  isPublished?: boolean;
}

export const TenantProfileApi = {
  get: () =>
    api<{ ok: true; data: { profile: TenantProfile } }>('/api/v1/tenant-profile').then((r) => r.data.profile),

  update: (payload: TenantProfilePatch) =>
    api<{ ok: true; data: { profile: TenantProfile } }>('/api/v1/tenant-profile', {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.profile),

  slugAvailability: (slug: string) =>
    api<{ ok: true; data: { available: boolean; reason?: 'invalid_format' | 'taken' } }>(
      '/api/v1/tenant-profile/slug-availability',
      { query: { slug } },
    ).then((r) => r.data),

  uploadHeroImage: (file: File) =>
    fileToBase64(file).then(({ imageBase64, mimeType }) =>
      api<{ ok: true; data: { profile: TenantProfile; heroImageUrl: string } }>('/api/v1/tenant-profile/hero-image', {
        method: 'POST',
        body: { imageBase64, mimeType },
      }).then((r) => r.data),
    ),
};

function fileToBase64(file: File): Promise<{ imageBase64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const base64 = result.split(',')[1] ?? '';
      resolve({ imageBase64: base64, mimeType: file.type });
    };
    reader.onerror = () => reject(reader.error ?? new Error('Kunne ikke lese filen.'));
    reader.readAsDataURL(file);
  });
}

// ─── Scans (v1) ───────────────────────────────────────────────────────
export const Scan = {
  qr: (code: string) =>
    api<{ ok: true; data: { scan: ScanEvent; result: { kind: string; bottle?: Bottle; productId?: string } } }>(
      '/api/v1/scan/qr',
      { method: 'POST', body: { code } },
    ).then((r) => r.data),

  barcode: (code: string, opts: { symbology?: string; includeStats?: boolean; candidateLimit?: number } = {}) =>
    api<{ ok: true; data: { scan: ScanEvent; result: BarcodeResolveResult } }>(
      '/api/v1/scan/barcode',
      { method: 'POST', body: { code, ...opts } },
    ).then((r) => r.data),

  label: (payload: { imageBase64?: string; imageUrl?: string; mimeType?: string; candidateLimit?: number; async?: boolean }) =>
    api<{ ok: true; data: unknown }>('/api/v1/scan/label', { method: 'POST', body: payload }).then((r) => r.data),

  get: (id: string) =>
    api<{ ok: true; data: { scan: ScanEvent } }>(`/api/v1/scan/${id}`).then((r) => r.data.scan),

  list: (query: { page?: number; limit?: number; kind?: ScanKind } = {}) =>
    api<{ ok: true; data: { scans: ScanEvent[] }; meta: { page: number; limit: number; total: number } }>(
      '/api/v1/scans',
      { query: query as Record<string, string | number | undefined> },
    ).then((r) => ({ scans: r.data.scans, meta: r.meta })),
};

// ─── Pairing ──────────────────────────────────────────────────────────
type PairingStartResult = { session?: PairingSession; job?: Job };
type PairingEnvelope = { session?: unknown; pairing?: unknown; job?: Job };

export const Pairing = {
  recipe: (text: string) =>
    api<{ ok: true; data: PairingEnvelope }>('/api/v1/pairing/recipe', {
      method: 'POST',
      body: { text },
    }).then((r): PairingStartResult => ({
      job: r.data.job,
      session: normalizePairingSession(r.data.session ?? r.data.pairing),
    })),

  plate: (payload: { imageBase64?: string; imageUrl?: string; mimeType?: string; async?: boolean }) =>
    api<{ ok: true; data: PairingEnvelope }>('/api/v1/pairing/plate', {
      method: 'POST',
      body: payload,
    }).then((r): PairingStartResult => ({
      job: r.data.job,
      session: normalizePairingSession(r.data.session ?? r.data.pairing),
    })),

  get: (id: string) =>
    api<{ ok: true; data: PairingEnvelope }>(`/api/v1/pairing/${id}`).then((r) =>
      requirePairingSession(r.data.session ?? r.data.pairing),
    ),
};

function requirePairingSession(raw: unknown): PairingSession {
  const session = normalizePairingSession(raw);
  if (!session) throw new Error('Pairing response did not include a session.');
  return session;
}

function normalizePairingSession(raw: unknown): PairingSession | undefined {
  if (!isRecord(raw)) return undefined;
  const extractedDish = isRecord(raw.extractedDish) ? raw.extractedDish : undefined;
  const methods = arrayOfStrings(extractedDish?.methods);
  const suggestions = Array.isArray(raw.suggestions)
    ? raw.suggestions
        .map((item) => normalizePairingSuggestion(item))
        .filter((item): item is NonNullable<ReturnType<typeof normalizePairingSuggestion>> => Boolean(item))
    : [];

  return {
    id: stringValue(raw.id) ?? '',
    tenantId: stringValue(raw.tenantId) ?? '',
    kind: raw.kind === 'plate' ? 'plate' : 'recipe',
    status: raw.status === 'queued' || raw.status === 'processing' || raw.status === 'failed' ? raw.status : 'ready',
    extractedDish: extractedDish
      ? {
          ...(extractedDish as unknown as PairingSession['extractedDish']),
          summary: stringValue(extractedDish.summary) ?? stringValue(extractedDish.rawText),
          rawText: stringValue(extractedDish.rawText),
          cuisine: stringValue(extractedDish.cuisine),
          weight: stringValue(extractedDish.weight),
          sauce: stringValue(extractedDish.sauce),
          cookingMethod: stringValue(extractedDish.cookingMethod) ?? (methods.length ? methods.join(', ') : undefined),
          flavors: arrayOfStrings(extractedDish.flavors),
          proteins: arrayOfStrings(extractedDish.proteins),
          methods,
        }
      : undefined,
    suggestions,
    createdAt: stringValue(raw.createdAt) ?? new Date().toISOString(),
  };
}

function normalizePairingSuggestion(raw: unknown): { bottleId: string; score: number; reasoning?: string } | undefined {
  if (!isRecord(raw)) return undefined;
  const bottle = isRecord(raw.bottle) ? raw.bottle : undefined;
  const bottleId = stringValue(raw.bottleId) ?? stringValue(bottle?.id);
  if (!bottleId) return undefined;
  return {
    bottleId,
    score: normalizePairingScore(raw.score),
    reasoning: stringValue(raw.reasoning) ?? stringValue(raw.explanation),
  };
}

function normalizePairingScore(value: unknown): number {
  const score = Number(value);
  if (!Number.isFinite(score)) return 0;
  return score > 1 ? Math.max(0, Math.min(1, score / 100)) : Math.max(0, Math.min(1, score));
}

function arrayOfStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.length > 0) : [];
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

// ─── Recommendations ──────────────────────────────────────────────────
export const Recs = {
  list: (lane?: 'drink-soon' | 'undiscovered' | 'pair-with', limit = 10) =>
    api<{ ok: true; data: { recommendations: RecommendationItem[]; bottles?: Bottle[]; source?: 'rules' | 'openai'; ai?: { enabled: boolean; used: boolean; mode?: string; error?: string } } }>(
      '/api/v1/recommendations',
      { query: { lane, limit } },
    ).then((r) => r.data),
};

// ─── Guides ───────────────────────────────────────────────────────────
export type GuideCategory = 'syrup' | 'garnish' | 'ice' | 'technique' | 'prep' | 'sober-curious';

export interface GuideStep {
  title?: string;
  body: string;
}

export interface Guide {
  slug: string;
  title: string;
  description: string;
  category: GuideCategory;
  totalMinutes: number;
  difficulty: number;
  tools: string[];
  ingredients: { name: string; amount?: string; notes?: string }[];
  steps: GuideStep[];
  unlocks: string[];
  yields?: string;
  keepsFor?: string;
  tags: string[];
}

export const Guides = {
  list: (
    opts: {
      category?: GuideCategory;
      tag?: string;
      forCocktail?: string;
    } = {},
  ) =>
    api<{ ok: true; data: { guides: Guide[]; total: number } }>('/api/v1/guides', {
      query: opts as Record<string, string | undefined>,
    }).then((r) => r.data),

  get: (slug: string) =>
    api<{ ok: true; data: { guide: Guide } }>(`/api/v1/guides/${encodeURIComponent(slug)}`).then(
      (r) => r.data.guide,
    ),
};

// ─── Stats + badges ───────────────────────────────────────────────────
export interface CategoryCount {
  key: string;
  label: string;
  count: number;
}
export interface MonthBucket {
  month: string;
  count: number;
  averageRating?: number;
}
export interface TopBottle {
  name: string;
  productId?: string;
  bottleId?: string;
  imageUrl?: string;
  count: number;
  averageRating?: number;
}
export interface UserStats {
  totalCheckIns: number;
  totalRated: number;
  averageRating?: number;
  uniqueWines: number;
  byCategory: CategoryCount[];
  byCountry: CategoryCount[];
  byRegion: CategoryCount[];
  byServingStyle: CategoryCount[];
  topBottles: TopBottle[];
  recent12Months: MonthBucket[];
  firstCheckIn?: string;
  latestCheckIn?: string;
  spanDays?: number;
  avgDaysBetween?: number;
}

export type BadgeTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  tier: BadgeTier;
  category: 'milestone' | 'explorer' | 'specialist' | 'social' | 'seasonal';
}

export interface UserBadge {
  definition: BadgeDefinition;
  earnedAt: string;
  count?: number;
}

export const Stats = {
  me: () =>
    api<{ ok: true; data: { stats: UserStats } }>('/api/v1/stats/me').then((r) => r.data.stats),
  household: () =>
    api<{ ok: true; data: { stats: UserStats } }>('/api/v1/stats/household').then((r) => r.data.stats),
};

export const Badges = {
  me: () =>
    api<{ ok: true; data: { badges: UserBadge[] } }>('/api/v1/badges/me').then((r) => r.data.badges),
  all: () =>
    api<{
      ok: true;
      data: { earned: UserBadge[]; locked: BadgeDefinition[] };
    }>('/api/v1/badges/me/all').then((r) => r.data),
  catalog: () =>
    api<{ ok: true; data: { definitions: BadgeDefinition[] } }>('/api/v1/badges/catalog').then(
      (r) => r.data.definitions,
    ),
};

// ─── Check-ins (Untappd-style drinking events) ────────────────────────
export type CheckInVisibility = 'private' | 'household' | 'friends' | 'public';

export interface CheckIn {
  id: string;
  tenantId: string;
  userId: string;
  bottleId?: string;
  productId?: string;
  rating?: number;
  comment?: string;
  photoUrl?: string;
  servingStyle?: string;
  venueName?: string;
  withFood?: string;
  snapshotName: string;
  snapshotImageUrl?: string;
  snapshotVintage?: string;
  snapshotRegion?: string;
  snapshotCountry?: string;
  visibility: CheckInVisibility;
  toastCount: number;
  /** Set on list/get responses for the current viewer — true if they have toasted this check-in. */
  viewerHasToasted?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CheckInCreate {
  bottleId?: string;
  productId?: string;
  rating?: number;
  comment?: string;
  photoUrl?: string;
  servingStyle?: string;
  venueName?: string;
  withFood?: string;
  visibility?: CheckInVisibility;
}

export const CheckIns = {
  list: (
    opts: {
      scope?: 'mine' | 'household' | 'bottle' | 'product';
      bottleId?: string;
      productId?: string;
      limit?: number;
    } = {},
  ) =>
    api<{ ok: true; data: { checkIns: CheckIn[] } }>('/api/v1/checkins', {
      query: opts as Record<string, string | number | undefined>,
    }).then((r) => r.data.checkIns),

  get: (id: string) =>
    api<{ ok: true; data: { checkIn: CheckIn } }>(`/api/v1/checkins/${id}`).then(
      (r) => r.data.checkIn,
    ),

  create: (payload: CheckInCreate) =>
    api<{ ok: true; data: { checkIn: CheckIn } }>('/api/v1/checkins', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.checkIn),

  update: (id: string, payload: Partial<CheckInCreate>) =>
    api<{ ok: true; data: { checkIn: CheckIn } }>(`/api/v1/checkins/${id}`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.checkIn),

  delete: (id: string) =>
    api<unknown>(`/api/v1/checkins/${id}`, { method: 'DELETE' }),

  toast: (id: string) =>
    api<{ ok: true; data: { toasted: true; toastCount: number; added: boolean } }>(
      `/api/v1/checkins/${id}/toast`,
      { method: 'POST' },
    ).then((r) => r.data),

  untoast: (id: string) =>
    api<{ ok: true; data: { toasted: false; toastCount: number; removed: boolean } }>(
      `/api/v1/checkins/${id}/toast`,
      { method: 'DELETE' },
    ).then((r) => r.data),
};

// ─── Bug reports ──────────────────────────────────────────────────────
export type BugReportSeverity = 'low' | 'normal' | 'high';

export interface BugReportCreate {
  area?: string;
  severity?: BugReportSeverity;
  message: string;
  url?: string;
  userAgent?: string;
  client?: Record<string, unknown>;
}

export interface BugReport {
  id: string;
  tenantId: string;
  userId: string;
  area: string;
  severity: BugReportSeverity;
  message: string;
  url?: string;
  userAgent?: string;
  client: Record<string, unknown>;
  createdAt: string;
}

export const BugReports = {
  create: (payload: BugReportCreate) =>
    api<{ ok: true; data: { report: BugReport } }>('/api/v1/bug-reports', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.report),
};

// ─── Community aggregates (cross-tenant) ──────────────────────────────
export interface CommunityTopRatedItem {
  productId: string;
  averageRating: number;
  ratingCount: number;
  snapshotName: string;
  snapshotImageUrl?: string;
  snapshotRegion?: string;
  snapshotCountry?: string;
  snapshotVintage?: string;
  latestAt: string;
}

export const Community = {
  topRated: (opts: { limit?: number; minCount?: number } = {}) =>
    api<{ ok: true; data: { items: CommunityTopRatedItem[] } }>(
      '/api/v1/community/top-rated',
      { query: opts as Record<string, string | number | undefined> },
    ).then((r) => r.data.items),
};

// ─── Wishlist ─────────────────────────────────────────────────────────
export interface WishlistItem {
  id: string;
  tenantId: string;
  userId: string;
  productId: string;
  name: string;
  vintage?: string;
  region?: string;
  country?: string;
  imageUrl?: string;
  priceNok?: number;
  note?: string;
  createdAt: string;
}

export const Wishlist = {
  list: () =>
    api<{ ok: true; data: { items: WishlistItem[] } }>('/api/v1/wishlist').then((r) => r.data.items),

  add: (payload: {
    productId: string;
    name: string;
    vintage?: string;
    region?: string;
    country?: string;
    imageUrl?: string;
    priceNok?: number;
    note?: string;
  }) =>
    api<{ ok: true; data: { item: WishlistItem } }>('/api/v1/wishlist', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.item),

  remove: (productId: string) =>
    api<unknown>(`/api/v1/wishlist/${encodeURIComponent(productId)}`, { method: 'DELETE' }),
};

// ─── Cocktails ────────────────────────────────────────────────────────
export interface CocktailIngredient {
  role: string;
  display: string;
  amount?: number;
  unit?: string;
  optional?: boolean;
  notes?: string;
}

export interface CocktailRecipe {
  slug: string;
  name: string;
  description: string;
  base: string;
  glassware: string;
  ice: string;
  garnish?: string;
  method: string[];
  difficulty: number;
  ibaClassic: boolean;
  tags: string[];
  ingredients: CocktailIngredient[];
}

export interface CocktailMatch {
  recipe: CocktailRecipe;
  canMake: boolean;
  missingCount: number;
  gaps: { role: string; display: string }[];
}

export interface CocktailListSummary {
  canMake: number;
  almost: number;
  total: number;
  availableRoles: string[];
  pantryDefaults: string[];
}

export interface ExternalCocktailIngredient {
  name: string;
  measure?: string;
}

export interface ExternalCocktailRecipe {
  provider: 'thecocktaildb';
  id: string;
  name: string;
  category?: string;
  alcoholic?: string;
  glass?: string;
  instructions?: string;
  thumbnailUrl?: string;
  ingredients: ExternalCocktailIngredient[];
}

export interface ExternalCocktailProviderStatus {
  name: string;
  enabled: boolean;
  auth: string;
  baseUrl: string;
}

export const Cocktails = {
  list: (
    opts: {
      filter?: 'all' | 'can-make' | 'almost';
      base?: string;
      tag?: string;
    } = {},
  ) =>
    api<{
      ok: true;
      data: { cocktails: CocktailMatch[]; summary: CocktailListSummary };
    }>('/api/v1/cocktails', {
      query: opts as Record<string, string | undefined>,
    }).then((r) => r.data),

  get: (slug: string) =>
    api<{ ok: true; data: { cocktail: CocktailMatch } }>(
      `/api/v1/cocktails/${encodeURIComponent(slug)}`,
    ).then((r) => r.data.cocktail),

  searchExternal: (q: string, limit = 8) =>
    api<{
      ok: true;
      data: {
        provider: ExternalCocktailProviderStatus;
        cocktails: ExternalCocktailRecipe[];
      };
    }>('/api/v1/cocktails/external/search', {
      query: { q, limit },
    }).then((r) => r.data),
};

// ─── Friends + per-friend share levels ───────────────────────────────
export type FriendStatus = 'pending_in' | 'pending_out' | 'accepted' | 'blocked';
export type FriendShareScope = 'cellar' | 'checkins' | 'wishlist' | 'tastings' | 'pantry';
export type FriendShareLevel = 'hidden' | 'counts' | 'full';

export interface FriendListItem {
  userId: string;
  email: string;
  displayName: string;
  status: FriendStatus;
  initiatedAt: string;
  respondedAt?: string;
}

export interface FriendPendingInvite {
  email: string;
  expiresAt: string;
  invitedAt: string;
}

export interface FriendsList {
  accepted: FriendListItem[];
  incoming: FriendListItem[];
  outgoing: FriendListItem[];
  pendingInvites: FriendPendingInvite[];
}

/** Result of POST /friends/request — discriminated by `kind`. */
export type FriendRequestResult =
  | { kind: 'link'; link: FriendListItem }
  | {
      kind: 'invite_sent';
      email: string;
      expiresAt: string;
      delivery: 'email' | 'disabled';
    };

export type FriendShareMap = Record<FriendShareScope, FriendShareLevel>;

export interface FriendCellarView {
  level: FriendShareLevel;
  friend: { userId: string; displayName: string };
  counts: { total: number };
  bottles?: {
    id: string;
    name: string;
    producer?: string;
    vintage?: string;
    region?: string;
    country?: string;
    category: BottleCategory;
    qty: number;
    status: BottleStatus;
    photoUrl?: string;
    tags: string[];
  }[];
}

export interface FriendCheckinsView {
  level: FriendShareLevel;
  friend: { userId: string; displayName: string };
  counts: { total: number };
  checkins?: {
    id: string;
    rating?: number;
    comment?: string;
    photoUrl?: string;
    snapshotName: string;
    snapshotImageUrl?: string;
    snapshotVintage?: string;
    snapshotRegion?: string;
    venueName?: string;
    withFood?: string;
    servingStyle?: string;
    visibility: 'private' | 'household' | 'friends' | 'public';
    toastCount: number;
    createdAt: string;
  }[];
}

export const Friends = {
  list: () =>
    api<{ ok: true; data: FriendsList }>('/api/v1/friends').then((r) => r.data),

  request: (email: string): Promise<FriendRequestResult> =>
    api<{ ok: true; data: FriendRequestResult }>('/api/v1/friends/request', {
      method: 'POST',
      body: { email },
    }).then((r) => r.data),

  cancelInvite: (email: string) =>
    api<{ ok: true }>('/api/v1/friends/invites', {
      method: 'DELETE',
      query: { email },
    }),

  accept: (userId: string) =>
    api<{ ok: true; data: { link: { status: FriendStatus } } }>(
      `/api/v1/friends/${encodeURIComponent(userId)}/accept`,
      { method: 'POST' },
    ).then((r) => r.data.link),

  remove: (userId: string) =>
    api<{ ok: true }>(`/api/v1/friends/${encodeURIComponent(userId)}`, { method: 'DELETE' }),

  shares: (userId: string) =>
    api<{ ok: true; data: { outgoing: FriendShareMap; incoming: FriendShareMap } }>(
      `/api/v1/friends/${encodeURIComponent(userId)}/share`,
    ).then((r) => r.data),

  setShares: (userId: string, shares: { scope: FriendShareScope; level: FriendShareLevel }[]) =>
    api<{ ok: true; data: { outgoing: FriendShareMap } }>(
      `/api/v1/friends/${encodeURIComponent(userId)}/share`,
      { method: 'PATCH', body: { shares } },
    ).then((r) => r.data.outgoing),

  cellar: (userId: string) =>
    api<{ ok: true; data: FriendCellarView }>(
      `/api/v1/friends/${encodeURIComponent(userId)}/cellar`,
    ).then((r) => r.data),

  checkins: (userId: string) =>
    api<{ ok: true; data: FriendCheckinsView }>(
      `/api/v1/friends/${encodeURIComponent(userId)}/checkins`,
    ).then((r) => r.data),
};

export const Users = {
  search: (email: string) =>
    api<{ ok: true; data: { users: { id: string; email: string; displayName: string }[] } }>(
      '/api/v1/users/search',
      { query: { q: email } },
    ).then((r) => r.data.users),
};

// ─── Bar inventory + pantry ───────────────────────────────────────────
export type PantryItemType = 'mixer' | 'citrus' | 'syrup' | 'garnish' | 'bitters' | 'ice' | 'other';

export type BarRole =
  | 'gin' | 'vodka' | 'whisky' | 'rum' | 'tequila' | 'mezcal' | 'brandy' | 'aquavit'
  | 'vermouth' | 'sherry' | 'port' | 'aperitif' | 'bitter' | 'liqueur'
  | 'wine' | 'beer' | 'cider' | 'sake' | 'mead' | 'other';

export type BarRoleGroup = 'base' | 'modifier' | 'table' | 'other';

export interface PantryItem {
  id: string;
  tenantId: string;
  type: PantryItemType;
  name: string;
  qty?: string;
  notes?: string;
  inStock: boolean;
  createdAt: string;
  updatedAt: string;
  etag: string;
}

export interface BarInventoryEntry {
  id: string;
  name: string;
  producer?: string;
  qty: number;
  photoUrl?: string;
  role: BarRole;
  inferred: boolean;
}

export interface BarInventory {
  bottles: {
    groups: Record<BarRoleGroup, BarInventoryEntry[]>;
    byRole: Partial<Record<BarRole, BarInventoryEntry[]>>;
  };
  pantry: {
    items: PantryItem[];
    byType: Record<PantryItemType, PantryItem[]>;
  };
  counts: { bottles: number; pantry: number };
}

export const Bar = {
  inventory: () =>
    api<{ ok: true; data: BarInventory }>('/api/v1/bar/inventory').then((r) => r.data),
};

export const Pantry = {
  list: () =>
    api<{ ok: true; data: { items: PantryItem[] } }>('/api/v1/bar/pantry').then((r) => r.data.items),

  create: (body: {
    type: PantryItemType;
    name: string;
    qty?: string;
    notes?: string;
    inStock?: boolean;
  }) =>
    api<{ ok: true; data: { item: PantryItem } }>('/api/v1/bar/pantry', {
      method: 'POST',
      body,
    }).then((r) => r.data.item),

  update: (
    id: string,
    body: Partial<{
      type: PantryItemType;
      name: string;
      qty: string | null;
      notes: string | null;
      inStock: boolean;
    }>,
  ) =>
    api<{ ok: true; data: { item: PantryItem } }>(`/api/v1/bar/pantry/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }).then((r) => r.data.item),

  delete: (id: string) =>
    api<{ ok: true }>(`/api/v1/bar/pantry/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ─── Discovery (vmpws/v2 consumer API via backend proxy) ──────────────
export interface VmpDiscoverProduct {
  code: string;
  name: string;
  url?: string;
  buyable?: boolean;
  expired?: boolean;
  status?: string;
  sustainable?: boolean;
  product_selection?: string;
  main_category?: { code: string; name: string };
  main_sub_category?: { code: string; name: string };
  main_country?: { code: string; name: string };
  district?: { code: string; name: string };
  sub_District?: { code: string; name: string };
  price?: { value: number; formattedValue?: string };
  volume?: { value: number; formattedValue?: string };
  alcohol?: { value: number; formattedValue?: string };
  images?: { format: string; url: string }[];
}

export interface VmpFacet {
  code: string;
  name: string;
  values: { code: string; name: string; count: number; selected: boolean }[];
}

export const Discover = {
  nyheter: (opts: { category?: string; pageSize?: number } = {}) =>
    api<{
      ok: true;
      data: { products: VmpDiscoverProduct[]; total: number; facets: VmpFacet[] };
    }>('/api/v1/catalog/nyheter', { query: opts as Record<string, string | number | undefined> }).then(
      (r) => r.data,
    ),

  search: (opts: {
    q?: string;
    category?: string;
    country?: string;
    sort?: 'relevance' | 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc' | 'alcohol-asc' | 'alcohol-desc';
    pageSize?: number;
    page?: number;
  } = {}) =>
    api<{
      ok: true;
      data: {
        products: VmpDiscoverProduct[];
        total: number;
        page: number;
        totalPages: number;
        facets: VmpFacet[];
        sorts?: { code: string; name: string; selected?: boolean }[];
      };
    }>('/api/v1/catalog/discover', { query: opts as Record<string, string | number | undefined> }).then(
      (r) => r.data,
    ),
};

export type CatalogSyncSource = 'hybrid' | 'vinmonopolet' | 'wineapi' | 'lwin';
export type CatalogSyncSelection = 'activity' | 'queries' | 'lwin' | 'mixed' | 'norway-common';

export interface CatalogSyncCandidate {
  query: string;
  category: BottleCategory;
  priority: number;
  reason: 'cellar' | 'wishlist' | 'checkin' | 'scan' | 'query' | 'lwin';
  sourceHint: CatalogSyncSource;
  sourceId?: string;
  productId?: string;
}

export interface CatalogSyncPreview {
  candidates: CatalogSyncCandidate[];
  policy: {
    source: CatalogSyncSource;
    selection: CatalogSyncSelection;
    categories: BottleCategory[];
    wineApi?: { enabled: boolean; configured: boolean; dailyLimit: number; baseUrl: string };
    lwinDatabase?: { enabled: boolean; exists: boolean; configuredPath?: string; rowCount: number };
    lwinTotalRows?: number;
  };
}

export const CatalogSync = {
  preview: (opts: {
    source?: CatalogSyncSource;
    selection?: CatalogSyncSelection;
    categories?: BottleCategory[];
    queries?: string[];
    limit?: number;
    lwinLimit?: number;
    lwinOffset?: number;
  } = {}) =>
    api<{ ok: true; data: CatalogSyncPreview }>('/api/v1/catalog/sync/preview', {
      query: {
        source: opts.source,
        selection: opts.selection,
        categories: opts.categories?.join(','),
        queries: opts.queries?.join('\n'),
        limit: opts.limit,
        lwinLimit: opts.lwinLimit,
        lwinOffset: opts.lwinOffset,
      },
    }).then((r) => r.data),

  start: (payload: {
    source: CatalogSyncSource;
    selection: CatalogSyncSelection;
    categories: BottleCategory[];
    queries?: string[];
    limitPerQuery?: number;
    planLimit?: number;
    requestBudget?: number;
    includeDetails?: boolean;
    lwinLimit?: number;
    lwinOffset?: number;
    runNow?: boolean;
  }) =>
    api<{ ok: true; data: { job: Job } }>('/api/v1/catalog/sync', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.job),

  backfill: (payload: {
    source?: Exclude<CatalogSyncSource, 'vinmonopolet'>;
    categories?: BottleCategory[];
    startOffset?: number;
    rowsPerDay?: number;
    days?: number;
    requestBudget?: number;
    limitPerQuery?: number;
    runFirstNow?: boolean;
  }) =>
    api<{ ok: true; data: { jobs: Job[]; totalRows: number; plannedRows: number; nextOffset: number } }>(
      '/api/v1/catalog/sync/backfill',
      { method: 'POST', body: payload },
    ).then((r) => r.data),
};

// ─── Jobs ─────────────────────────────────────────────────────────────
export const Jobs = {
  list: (query: { limit?: number; type?: string; status?: string } = {}) =>
    api<{ ok: true; data: { jobs: Job[] }; meta?: { page: number; limit: number; total: number } }>(
      '/api/v1/jobs',
      { query: query as Record<string, string | number | undefined> },
    ).then((r) => ({ jobs: r.data.jobs, meta: r.meta })),

  get: (id: string) =>
    api<{ ok: true; data: { job: Job } }>(`/api/v1/jobs/${id}`).then((r) => r.data.job),

  run: (id: string) =>
    api<{ ok: true; data: { job: Job } }>(`/api/v1/jobs/${id}/run`, { method: 'POST' }).then((r) => r.data.job),
};

// ─── Kjellerhistorikk (cellar events) ───────────────────────────────────
export type CellarEventActivity = 'added' | 'drunk' | 'gifted' | 'sold' | 'adjusted' | 'moved' | 'received' | 'counted';

export interface CellarEvent {
  id: string;
  tenantId: string;
  bottleId?: string;
  wineName: string;
  activity: CellarEventActivity;
  quantityDelta: number;
  remainingAfter: number;
  purchasePriceNok?: number;
  sellingPriceNok?: number;
  note?: string;
  actorUserId?: string;
  createdAt: string;
}

export const CellarEvents = {
  list: (
    opts: {
      activity?: CellarEventActivity;
      bottleId?: string;
      from?: string;
      to?: string;
      page?: number;
      limit?: number;
    } = {},
  ) =>
    api<{ ok: true; data: { events: CellarEvent[] }; meta?: { page: number; limit: number; total: number } }>(
      '/api/v1/cellar-events',
      { query: opts as Record<string, string | number | undefined> },
    ).then((r) => ({ events: r.data.events, meta: r.meta })),
};

// ─── Bestilling (purchase orders) ───────────────────────────────────────
export type PurchaseOrderStatus = 'draft' | 'ordered' | 'received' | 'cancelled';

export interface PurchaseOrder {
  id: string;
  tenantId: string;
  supplier?: string;
  status: PurchaseOrderStatus;
  note?: string;
  orderedAt?: string;
  receivedAt?: string;
  createdAt: string;
  updatedAt: string;
  etag: string;
}

export interface PurchaseOrderLine {
  id: string;
  tenantId: string;
  orderId: string;
  catalogId?: string;
  name: string;
  producer?: string;
  vintage?: string;
  region?: string;
  country?: string;
  imageUrl?: string;
  quantity: number;
  unitPriceNok?: number;
  bottleId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderLineInput {
  catalogId?: string;
  name: string;
  producer?: string;
  vintage?: string;
  region?: string;
  country?: string;
  imageUrl?: string;
  quantity: number;
  unitPriceNok?: number;
}

export const Orders = {
  list: () => api<{ ok: true; data: { orders: PurchaseOrder[] } }>('/api/v1/orders').then((r) => r.data.orders),

  create: (payload: { supplier?: string; note?: string }) =>
    api<{ ok: true; data: { order: PurchaseOrder } }>('/api/v1/orders', { method: 'POST', body: payload }).then(
      (r) => r.data.order,
    ),

  get: (id: string) =>
    api<{ ok: true; data: { order: PurchaseOrder; lines: PurchaseOrderLine[] } }>(`/api/v1/orders/${id}`).then(
      (r) => r.data,
    ),

  update: (id: string, payload: { supplier?: string; note?: string }, etag?: string) =>
    api<{ ok: true; data: { order: PurchaseOrder } }>(`/api/v1/orders/${id}`, {
      method: 'PATCH',
      body: payload,
      headers: etag ? { 'If-Match': etag } : undefined,
    }).then((r) => r.data.order),

  delete: (id: string) => api<unknown>(`/api/v1/orders/${id}`, { method: 'DELETE' }),

  addLine: (orderId: string, payload: PurchaseOrderLineInput) =>
    api<{ ok: true; data: { line: PurchaseOrderLine } }>(`/api/v1/orders/${orderId}/lines`, {
      method: 'POST',
      body: payload,
    }).then((r) => r.data.line),

  updateLine: (orderId: string, lineId: string, payload: Partial<PurchaseOrderLineInput>) =>
    api<{ ok: true; data: { line: PurchaseOrderLine } }>(`/api/v1/orders/${orderId}/lines/${lineId}`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.line),

  deleteLine: (orderId: string, lineId: string) =>
    api<unknown>(`/api/v1/orders/${orderId}/lines/${lineId}`, { method: 'DELETE' }),

  place: (orderId: string) =>
    api<{ ok: true; data: { order: PurchaseOrder; lines: PurchaseOrderLine[] } }>(`/api/v1/orders/${orderId}/order`, {
      method: 'POST',
    }).then((r) => r.data),

  receive: (orderId: string, payload: { locationId?: string } = {}) =>
    api<{ ok: true; data: { order: PurchaseOrder; lines: PurchaseOrderLine[] } }>(`/api/v1/orders/${orderId}/receive`, {
      method: 'POST',
      body: payload,
    }).then((r) => r.data),

  cancel: (orderId: string) =>
    api<{ ok: true; data: { order: PurchaseOrder; lines: PurchaseOrderLine[] } }>(`/api/v1/orders/${orderId}/cancel`, {
      method: 'POST',
    }).then((r) => r.data),
};

// ─── Varetelling (stock counts) ─────────────────────────────────────────
export type StockCountStatus = 'open' | 'completed';

export interface StockCount {
  id: string;
  tenantId: string;
  status: StockCountStatus;
  note?: string;
  startedAt: string;
  completedAt?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  etag: string;
}

export interface StockCountLine {
  id: string;
  tenantId: string;
  countId: string;
  bottleId: string;
  name: string;
  producer?: string;
  vintage?: string;
  region?: string;
  country?: string;
  category: BottleCategory;
  locationId?: string;
  purchasePriceNok?: number;
  expectedQuantity: number;
  countedQuantity?: number;
  confirmed: boolean;
  createdAt: string;
  updatedAt: string;
}

export const StockCounts = {
  list: () => api<{ ok: true; data: { counts: StockCount[] } }>('/api/v1/stock-counts').then((r) => r.data.counts),

  create: (payload: { note?: string } = {}) =>
    api<{ ok: true; data: { count: StockCount; lines: StockCountLine[] } }>('/api/v1/stock-counts', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data),

  get: (id: string) =>
    api<{ ok: true; data: { count: StockCount; lines: StockCountLine[] } }>(`/api/v1/stock-counts/${id}`).then(
      (r) => r.data,
    ),

  updateLine: (countId: string, lineId: string, payload: { countedQuantity?: number | null; confirmed?: boolean }) =>
    api<{ ok: true; data: { line: StockCountLine } }>(`/api/v1/stock-counts/${countId}/lines/${lineId}`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data.line),

  complete: (countId: string, apply: boolean) =>
    api<{ ok: true; data: { count: StockCount; lines: StockCountLine[]; adjustedCount: number } }>(
      `/api/v1/stock-counts/${countId}/complete`,
      { method: 'POST', body: { apply } },
    ).then((r) => r.data),

  pdfUrl: (id: string) => `${apiBase}/api/v1/stock-counts/${id}/pdf`,
};

// ─── POS sales-feed ("POS integrasjon", a Pro capability) ───────────────
export interface PosApiKey {
  id: string;
  name: string;
  /** Non-secret — e.g. "pos_live_a1b2c3d4". Shown in the list. */
  prefix: string;
  lastUsedAt?: string;
  revokedAt?: string;
  createdAt: string;
}

export type PosSaleStatus = 'applied' | 'partial' | 'not_matched' | 'duplicate';

export interface PosSaleLineInput {
  externalId: string;
  bottleId?: string;
  catalogId?: string;
  name?: string;
  quantity: number;
  unitPriceNok?: number;
  soldAt?: string;
}

export interface PosSaleLineResult {
  externalId: string;
  status: PosSaleStatus;
  bottleId?: string;
  requestedQuantity: number;
  appliedQuantity: number;
  warning?: string;
}

export const Pos = {
  listKeys: () => api<{ ok: true; data: { keys: PosApiKey[] } }>('/api/v1/pos/keys').then((r) => r.data.keys),

  createKey: (name: string) =>
    api<{ ok: true; data: { apiKey: PosApiKey; plaintextKey: string } }>('/api/v1/pos/keys', {
      method: 'POST',
      body: { name },
    }).then((r) => r.data),

  revokeKey: (id: string) => api<unknown>(`/api/v1/pos/keys/${id}/revoke`, { method: 'POST' }),

  /** Same endpoint an external POS calls — exposed here only so the /pos integration docs' "prøv selv" curl example can be tested against a locally-created key from the browser console if needed. Normal traffic to this endpoint comes from the POS system itself, not the SPA. */
  ingestUrl: () => `${apiBase}/api/v1/pos/sales`,
};

// ─── Market value (self-owned — no Liv-ex / third-party pricing service) ─
export interface MarketValueCrossMarketRow {
  catalogId: string;
  source: string;
  name: string;
  priceNok: number;
  priceUpdatedAt?: string;
  confidence: 'ean' | 'name_vintage';
}

export interface MarketValueSeriesPoint {
  /** "YYYY-MM" */
  month: string;
  priceNok: number;
}

export interface CatalogMarketValue {
  catalogId: string;
  name: string;
  producer?: string;
  vintage?: string;
  source: string;
  currentPriceNok?: number;
  minPriceNok?: number;
  maxPriceNok?: number;
  firstPriceNok?: number;
  firstCapturedAt?: string;
  lastCapturedAt?: string;
  trend?: 'up' | 'down' | 'flat';
  series: MarketValueSeriesPoint[];
  crossMarket: MarketValueCrossMarketRow[];
}

export interface CellarMarketValue {
  totalMarketValueNok: number;
  totalPurchaseValueNok: number;
  matchedBottles: number;
  totalBottles: number;
}

export const MarketValue = {
  forCatalog: (catalogId: string) =>
    api<{ ok: true; data: CatalogMarketValue }>(`/api/v1/market-value/${catalogId}`).then((r) => r.data),

  forCellar: () => api<{ ok: true; data: CellarMarketValue }>('/api/v1/market-value/cellar').then((r) => r.data),
};

// re-export the unwrap helper if needed elsewhere
export { v1 };
